import { Col, Container, Row } from 'react-bootstrap';
import { Dispatch, SetStateAction } from 'react';
import { DispatchSummaryCallVolumeEnum, SortDirectionEnum } from '@/models/gen/graphql';
import { TODAY, TODAY_EOD } from '@/constants';
import { refetchDispatchSummaryWithMarketComments } from './api/services/searchDispatchSummary';

import { DispatchSummaryWithMarketComments } from './components/SummaryHistoryForm/types';
import Feature from '@/common/Feature';
import SummaryHistoryFilters from '@/features/SummaryHistory/components/SummaryHistoryFilters';
import SummaryHistoryTable from '@/features/SummaryHistory/components/SummaryHistoryTable';
import { useProvider } from '@/common/Provider';
import { ServiceIssueFiltersType } from '@/hooks/useServiceIssueFilters';

const SummaryHistory = (): JSX.Element => {
  return (
    <Feature name="SummaryHistory" context={initSummaryHistoryState} methods={summaryHistoryMethodConstructor}>
      <SummaryHistoryFilters />
      <Container>
        <Row>
          <Col>
            <SummaryHistoryTable />
          </Col>
        </Row>
      </Container>
    </Feature>
  );
};

export type SummaryHistoryFiltersType = {
  startDatetime?: string;
  endDatetime?: string;
  callVolumes: DispatchSummaryCallVolumeEnum[];
};
export type SummaryHistoryRow = DispatchSummaryWithMarketComments;
export type SummaryHistoryContext = {
  filters: ServiceIssueFiltersType;
  summaries: Map<string, SummaryHistoryRow>;
  totalSummaries: number;
  selected: Map<string, SummaryHistoryRow>;
  loading: boolean;
  sortBy: string;
  sortDirection: SortDirectionEnum;
  search: string;
};
export type SummaryHistoryMethods = {
  resetState: (overrides?: Partial<SummaryHistoryContext>) => void;
  submitFilters: (values?: Partial<SummaryHistoryFiltersType>) => Promise<void>;
};
export const initSummaryHistoryState: SummaryHistoryContext = {
  filters: {
    startDatetime: TODAY,
    endDatetime: TODAY_EOD,
    callVolumes: [],
  },
  search: '',
  summaries: new Map(),
  totalSummaries: 0,
  selected: new Map(),
  sortBy: '',
  sortDirection: SortDirectionEnum.Asc,
  loading: false,
};
export const summaryHistoryMethodConstructor = (
  state: SummaryHistoryContext,
  setState: Dispatch<SetStateAction<SummaryHistoryContext>>
): SummaryHistoryMethods => ({
  resetState: (overrides?: Partial<SummaryHistoryContext>): void => setState({ ...initSummaryHistoryState, ...(overrides || {}) }),
  submitFilters: async (values: ServiceIssueFiltersType = state.filters): Promise<void> => {
    try {
      setState((prevState) => ({ ...prevState, loading: true }));
      const res = await refetchDispatchSummaryWithMarketComments([values]);
      setState((current) => {
        const summaries = new Map<string, SummaryHistoryRow>(res.rows.map((issue) => [issue.id, issue]));
        const totalSummaries = res.totalCount;
        return { ...current, summaries, totalSummaries };
      });
    } catch (err) {
      console.error('Error submitting filters:', err);
    } finally {
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  },
});
export function useSummaryHistory<T = SummaryHistoryContext>(
  selector?: (state: SummaryHistoryContext) => T
): [T, Dispatch<SetStateAction<SummaryHistoryContext>>, SummaryHistoryMethods] {
  return useProvider<SummaryHistoryContext, T, SummaryHistoryMethods>(selector);
}

export default SummaryHistory;
